<template>
  <section class="body">
    <!-- Progress bar -->
    <div
      v-if="loadingFormData"
      class="text-center mb-2"
    >
      <b-progress
        :value="loadingFormDataValue"
        show-progress
        class="m-5"
      />
    </div>

    <b-form
      v-else
      class="formclass"
      @submit.prevent="submit"
    >
      <b-form-row class="mb-2">
        <AllDatesModal :form="form" />
        <SiteModal :form="form" />
        <TargetGroupModal
          :chosen-period="selectedPeriod"
          :field-data="form.target_group"
          @onGoToListTab="goToListTab = true"
        />
        <TitleModal :field-data="form.title" />
        <GenreModal :field-data="form.genres" />
        <CheckboxDropdownColumnsPrograms
          id="column-select"
          ref="optionsList"
          :form-field="form.columns"
          name="Columns"
          @setDefaultPayload="setDefaultPayload"
        />
        <button
          id="showMoreFilteringButton"
          class="btn btnclass smallerfontsize mr-3 showMoreFilteringButton"
          size="sm"
          type="button"
          @click="showMoreFilterings = !showMoreFilterings"
        >
          {{ showMoreFilterings ? 'Show less' : 'Show more' }}
          <b-icon-chevron-up v-if="showMoreFilterings" />
          <b-icon-chevron-down v-else />
        </button>
      </b-form-row>
      <b-form-row
        v-if="showMoreFilterings"
        class="mb-2 justify-content-center"
      >
        <!-- Categories - dropdown -->
        <b-col
          sm="6"
          md="4"
          lg="2"
          class="mb-2 inputwidthshowmorefields"
        >
          <label for="categories-select">Categories</label>
          <CheckboxDropdownCategories
            id="categories-select"
            ref="categoriesDropdown"
            :form-field="form.categories"
            name="Categories"
            @input="updateCategoryIds"
          />
        </b-col>

        <!-- Length (min) - input -->
        <b-col
          sm="6"
          md="4"
          lg="2"
          class="mb-2 inputwidthshowmorefields"

        >
          <label for="length-input">Length (min)</label>
          <b-form-input
            id="length-input"
            v-model="selectedTitleLength"
            v-b-tooltip.hover
            title="Enter in one of the following formats: <100, >100 or 100-1000"
            size="sm"
            :state="formValidator.title_length"
            @change="updateTitleLength"
          />
          <b-form-invalid-feedback id="length-input-feedback">
            Enter correct format
          </b-form-invalid-feedback>
        </b-col>

        <!-- Number of measurement days - input -->
        <b-col
          sm="6"
          md="4"
          lg="2"
          class="mb-2 inputwidthshowmorefields"
        >
          <label for="measurement-day-input">Number of days measured</label>
          <b-form-input
            id="measurement-day-input"
            v-model="selectedMeasuredDays"
            v-b-tooltip.hover
            title="Enter in one of the following formats: <100, >100 or 100-1000"
            size="sm"
            :state="formValidator.measured_days"
            @change="updateMeasuredDays"
          />
          <b-form-invalid-feedback id="measurement-day-input-feedback">
            Enter correct format
          </b-form-invalid-feedback>
        </b-col>

        <!-- Sum Starts - input -->
        <b-col
          sm="6"
          md="4"
          lg="2"
          class="mb-2 inputwidthshowmorefields"
        >
          <label for="start-input">Sum Starts</label>
          <b-form-input
            id="start-input"
            v-model="selectedSumStarts"
            v-b-tooltip.hover
            title="Enter in one of the following formats: <100, >100 or 100-1000"
            size="sm"
            :state="formValidator.starts"
            @change="updateTotalStarts"
          />
          <b-form-invalid-feedback id="start-input-feedback">
            Enter correct format
          </b-form-invalid-feedback>
        </b-col>

        <!-- Rating - input -->
        <b-col
          sm="6"
          md="4"
          lg="2"
          class="mb-2 inputwidthshowmorefields"
        >
          <label for="online-rating-input">Rating</label>
          <b-form-input
            id="online-rating-input"
            v-model="selectedTotalOnlineRating"
            v-b-tooltip.hover
            title="Enter in one of the following formats: <100, >100 or 100-1000"
            size="sm"
            :state="formValidator.total_online_rating"
            @change="updateTotalOnlineRating"
          />
          <b-form-invalid-feedback id="online-rating-input-feedback">
            Enter correct format
          </b-form-invalid-feedback>
        </b-col>
      </b-form-row>
      <b-form-row class="mb-2">
        <button
          v-if="getSelectedInputs > 0"
          type="button"
          class="trashbtn mt-2 mr-2"
          @click="clearAllFields"
        >
          <b-icon-trash />
        </button>
        <div
          v-for="(selectedButton, key) in selectedBtns"
          :key="key"
        >
          <button
            class="selectedbtns mr-2 mt-2 bubblefontsize"
            type="button"
            @click="selectedButton.clickedBtn && selectedButton.clickedBtn()"
          >
            <span v-html="selectedButton.text" />
            <b-icon
              v-if="selectedButton.editable"
              class="ml-2 crossbtn"
              font-scale="1.5"
              icon="x"
              @click="
                selectedButton.clickedBtnDelete &&
                  selectedButton.clickedBtnDelete($event)
              "
            />
          </button>
        </div>
      </b-form-row>
      <b-form-row class="mb-2">
        <div>
          <b-button
            :disabled="disableRunBtn"
            type="submit"
            class="searchbutton ml-3"
            variant="primary"
            size="sm"
          >
            <b-icon
              v-if="!loadingSearchResults"
              icon="arrow-repeat"
            />
            {{ loadingSearchResults ? 'Working...' : 'Run' }}
          </b-button>
        </div>
      </b-form-row>
    </b-form>

    <!-- Results -->
    <template v-if="initFirstSearch">
      <!-- Show if loading results -->
      <div
        v-if="!hasData && loadingSearchResults"
        class="text-center mb-2"
      >
        <hr>
        <b-spinner
          variant="primary"
          type="grow"
          label="Spinning"
        />
        <div>
          <small class="text-muted">Loading search results...</small>
        </div>
      </div>

      <div>
        <!-- Show if search have hits -->
        <Result
          v-show="hasData"
          :disabled="loadingSearchResults"
          :go-to-list-tab="goToListTab"
          :class="{ 'overlay-disabled': loadingSearchResults }"
          @submit-form="submit"
          @load-whole-table="loadWholeTable"
          @sort-only="submitSort"
          @exclude-rows="submitExcludeRows"
          @resetGoToListTab="goToListTab = false"
        />

        <!-- Show if search have no hits -->
        <div
          v-if="!hasData && !loadingSearchResults"
          class="text-center"
        >
          <hr>
          <p>No results</p>
          <p class="supportTextNoAnswers">
            You can try adjusting the conditions above to include more titles.
            <span
              v-if="isWildcardSearch"
            >Also note that you have the "wildcard support" option checked in the Title field.</span>
          </p>
        </div>
      </div>
    </template>
    <SlowQueryModal 
      :show="showSlowQueryModal" 
      :loading="loadingExportResults" 
      @download="handleExportResults"
      @cancel="showSlowQueryModal = false" 
    />
    <ScheduleReportModal 
      :show="showScheduleReportModal" 
      :loading="loadingScheduleReport" 
      @submit="handleScheduleReportSubmit" 
      @cancel="showScheduleReportModal = false" 
    />
  </section>
</template>

<script>
import SlowQueryModal from "../../Shared/SlowQueryModal/SlowQueryModal.vue"
import ScheduleReportModal from "../../Shared/ScheduleReportModal/ScheduleReportModal.vue"
import 'vue2-datepicker/index.css';
import Result from './Result.vue';
import { downloadFile, createScheduledReportQuery } from '../../../assets/js/api.js';
import AllDatesModal from './AllDatesModal';
import SiteModal from '@/components/Shared/SiteModal';
import TitleModal from './TitleModal';
import TargetGroupModal from './TargetGroupModal';
import GenreModal from '../Genres/GenreModal.vue';
import CheckboxDropdownCategories from '../../Shared/CheckboxDropdownCategories.vue';
import CheckboxDropdownColumnsPrograms from './CheckboxDropdownColumnsPrograms.vue';
import {
  dateToyymmddFormat,
  addDays,
  filterMeasuresByPeriod,
  processResults,
  sleep,
  getUniqueDates
} from '../../../assets/js/utils.js';
//custom scripts
import { search, fetchFormFieldData } from '../../../assets/js/api.js';
import { mapActions } from 'vuex';

export default {
  name: 'Program',
  components: {
    SlowQueryModal,
    ScheduleReportModal,
    AllDatesModal,
    SiteModal,
    TitleModal,
    TargetGroupModal,
    GenreModal,
    CheckboxDropdownCategories,
    CheckboxDropdownColumnsPrograms,
    Result,
  },
  data() {
    return {
      goToListTab: false,
      showScheduleReportModal: false,
      loadingScheduleReport: false,
      loadingExportResults:false,
      showSlowQueryModal: false,
      toolTipPeriod: {
        title:
          'Publication period = <br/> Shows consumption for titles with a publication date within the period. <br/> <br/> Consumption period = <br/>Shows consumption for ALL titles within the period and any publication interval.',
      },
      optionsPeriod: [
        { value: 'publicationperiod', text: 'Publication period' },
        { value: 'consumptionperiod', text: 'Consumption period' },
      ],
      showMoreFilterings: false,
      loadingFormData: true,
      loadingFormDataValue: 0,
      loadingFormDataMaxValue: 3,
      loadingSearchResults: false,
      validationError: false,
      initFirstSearch: false,
      form: {
        date: {
          datePicker: {      
            weekdays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
            months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          },
          showAllDatesModal: false
        },
        columns: {
          options: [],
          resetDefaultSelected: false,
        },
        sites: {
          options: [],
          selected: [],
          resetDefaultSelected: false,
          showSiteModal: false,
          apiBodyGetter: "searchApiBody/getPayload",
          apiBodySetter: "searchApiBody/updatePayload"
        },
        categories: {
          options: [],
          resetDefaultSelected: false
        },
        genres: {
          showGenreModal: false,
          genreOptionArray: [],
        },
        target_group: {
            measurements: {
            options: [],
            selected: ''
          },
          showTargetGroupModal: false
        },
        title: {
          showTitleModal: false
        },
        filters: {
          title_length: '',
          measured_days: '',
          starts: '',
          total_online_rating: '',
          tv_rating: '',
        },
      },
      formValidator: {
        dateFrom: true,
        dateTo: true,
        title_length: null,
        measured_days: null,
        starts: null,
        total_online_rating: null,
        tv_rating: null,
      },
      selectedBtns: []
    };
  },
  computed: {
    selectedTitleLength: {
      get() {
        let formattedTitle = this.formatRange(this.$store.state.searchApiBody.payload.filters.title_length);
        return formattedTitle;
      },
      set(value) {
        this.updateFilter(value, 'searchApiBody/updateTitleLength', 'title_length');
      },
    },
    selectedMeasuredDays:{
      get() {
        let formattedMeasuredDays = this.formatRange(this.$store.state.searchApiBody.payload.filters.measured_days);
        return formattedMeasuredDays;
      },
      set(value) {
        this.updateFilter(value, 'searchApiBody/updateMeasuredDays', 'measured_days');
      },
    },
    selectedSumStarts:{
      get() {
        let formattedSumStarts = this.formatRange(this.$store.state.searchApiBody.payload.filters.total_contacts);
        return formattedSumStarts;
      },
      set(value) {
        this.updateFilter(value, 'searchApiBody/updateTotalStarts', 'total_contacts');
      },
    },
    selectedTotalOnlineRating:{
      get() {
        let formattedTotalOnlineRating = this.formatRange(this.$store.state.searchApiBody.payload.filters.total_online_rating);
        return formattedTotalOnlineRating;
      },
      set(value) {
        this.updateFilter(value, 'searchApiBody/updateTotalOnlineRating', 'total_online_rating');
      },
    },
    isWildcardSearch() {
      return this.$store.state.searchApiBody.payload.isWildcard;
    },
    selectedPeriod() {
      return this.$store.state.searchApiBody.payload.selectedPeriod;
    },
    allModalsClosed() {
      let allModalsClosed = false;
      if (
        !this.form.date.showAllDatesModal &&
        !this.form.genres.showGenreModal &&
        !this.form.sites.showSiteModal &&
        !this.form.target_group.showTargetGroupModal &&
        !this.form.title.showTitleModal
      )
        allModalsClosed = true;
      return allModalsClosed;
    },
    getSelectedInputs() {
      let selectedInputs = this.selectedBtns.length;
      return selectedInputs;
    },
    disableRunBtn() {
       let disableRunBtn = false;
       if (
          this.loadingSearchResults || 
          this.validationError || 
          this.selectedColumns.length === 0
      ) 
        disableRunBtn = true;
       return disableRunBtn;
    },
    hasData() {
      return this.$store.state.searchResults.results.length > 0;
    },
    selectedColumns() {
      return this.$store.state.searchApiBody.payload.columns;
    },
    title_search: {
      get() {
        return this.$store.state.searchApiBody.payload.title_search;
      },
      set(value) {
        this.$store.commit('searchApiBody/updateTitleSearch', value);
      },
    },
    payload(){
      return this.$store.getters["searchApiBody/getPayload"]
    }
  },
  watch:{
    "$store.state.systemInfo.publishedToDate"(pubToDate){
      this.setDefaultStartDateEndDate(pubToDate)
    },
    payload: {
      handler() {
        let query = this.$store.state.searchApiBody.payload
        let selectedBtns = [];
        if(this.selectedPeriod === "consumptionperiod")
          selectedBtns.push(
          {
            text: `<span class="font-weight-bold">Consumption date:</span> ${query.start_date} - ${query.end_date}`,
            editable: false,
            clickedBtn: () => {
              this.form.date.showAllDatesModal = true;
            }
          })
        if(this.selectedPeriod === "publicationperiod" || query.pub_start_date && query.pub_end_date)
          selectedBtns.push(
          {
            text: `<span class="font-weight-bold">Publication date:</span> ${query.start_date} - ${query.end_date}`,
            editable: true,
            clickedBtn: () => {
              this.form.date.showAllDatesModal = true;
            },
            clickedBtnDelete: event => {
              event.stopPropagation();
              this.resetPubDate();
            }
          })
        if (query.site_id) {
          selectedBtns.push({
            text: `<span class="font-weight-bold">Site:</span> (${query.site_id.length} chosen)`,
            clickedBtn: () => {
              this.form.sites.showSiteModal = true;
            }
          });
        }
        let numberOfStoredTargets = this.getTargetGroupCount();
        if (numberOfStoredTargets > 0) {
          selectedBtns.push({
            text: `<span class="font-weight-bold"> Target / Metric </span> ${numberOfStoredTargets} chosen`,
            editable: true,
            clickedBtn: () => {
              this.form.target_group.showTargetGroupModal = true;
            },
            clickedBtnDelete: event => {
              event.stopPropagation();
              this.removeTargets()
            }
          });
        }
        if(query.title_search){
          let titleText = `<span class="font-weight-bold">Title:</span> ${query.title_search}`;
          if (titleText.length > 70)
            titleText = titleText.substring(0, 70) + "...";
          selectedBtns.push({
            text: titleText,
            editable: true,
            clickedBtn: () => {
              this.form.title.showTitleModal = true;
            },
            clickedBtnDelete: event => {
              event.stopPropagation();
              let updatedPayload = {
                ...this.$store.getters["searchApiBody/getPayload"],
                title_search: ""
              };
              this.$store.commit(
                'searchApiBody/updatePayload',
                updatedPayload,
              );                    
            }
          });
        }
        let numberOfStoredGenres = this.getNumberOfStoredGenres();
        if (numberOfStoredGenres > 0) {
          selectedBtns.push({
            text: `<span class="font-weight-bold">Genre:</span> ${numberOfStoredGenres} chosen`,
            editable: true,
            clickedBtn: () => {
              this.form.genres.showGenreModal = true;
            },
            clickedBtnDelete: event => {
              event.stopPropagation();
              this.removeGenres();
            }
          });
        }
        this.selectedBtns = selectedBtns;
      },
      deep: true
    }
  },
  beforeDestroy() {
    // Clean up by removing the event listener when the component is destroyed
    document.removeEventListener("keyup", this.onKeyUp);
  },
  mounted() {
    document.addEventListener("keyup", this.onKeyUp);
    if (!this.$store.state.systemInfo.fullAccess) {
      this.$router.push('/');
    }
  },
  async created() {
    this.$store.commit('searchApiBody/updateCategoryIds', []);
    //Fetch sites from api
    this.setDefaultSites();

    //Fetch columns from api
    let columnsResponse = await fetchFormFieldData('ColumnTypes');
    if (columnsResponse.statusCode) {
      this.$bvToast.toast(columnsResponse.errorMessage, {
        title: 'Warning',
        variant: 'warning',
        solid: true,
        autoHideDelay: 3000,
      });
    } else {
      let columnsSelected = [];
      for (let index in columnsResponse) {
        let selected =
          columnsSelected.length > 0
            ? columnsSelected.includes(columnsResponse[index].column_type)
            : columnsResponse[index].default_selection;
        let column = {
          text: columnsResponse[index].display_name,
          value: columnsResponse[index].column_type,
          selected,
        };
        this.form.columns.options.push(column);
      }
      this.loadingFormDataValue = 50;
    }

    //Fetch categories from api
    let categoriesResponse = await fetchFormFieldData('Categories');

    if (categoriesResponse.statusCode) {
      this.$bvToast.toast(categoriesResponse.errorMessage, {
        title: 'Warning',
        variant: 'warning',
        solid: true,
        autoHideDelay: 3000,
      });
    } else {
      for (let index in categoriesResponse) {
        let category = {
          text: categoriesResponse[index].category_name,
          value: categoriesResponse[index].category_id,
        };
        this.form.categories.options.push(category);
      }
      this.loadingFormDataValue = 75;
    }

    //Fetch measures types from api
    let measureTypeResponse = await fetchFormFieldData('MeasureTypes');
    if (measureTypeResponse.statusCode) {
      this.$bvToast.toast(measureTypeResponse.errorMessage, {
        title: 'Warning',
        variant: 'warning',
        solid: true,
        autoHideDelay: 3000,
      });
    } else {
      for (let index in measureTypeResponse) {
        let measure = {
          text: measureTypeResponse[index].display_name,
          value: measureTypeResponse[index].measure_type,
          reachAbsHeader: measureTypeResponse[index].reach_abs_header,
          reachRelHeader: measureTypeResponse[index].reach_rel_header,
          showReachSelector: measureTypeResponse[index].show_reach_selector,
        };
        this.form.target_group.measurements.options.push(measure);
      }
      this.form.target_group.measurements.selected = measureTypeResponse[0].measure_type;

      this.loadingFormDataValue = 100;
    }

    // Fetch genre options from api
    this.setupGenreOptions();
    // Sleep so that the progress bar has time to finnish
    await sleep(600);
    let siteValues = this.form.sites.options.map(s => s.value)
    this.updateSiteIds(siteValues);
    this.$store.commit('systemInfo/updateAnyFormFieldsLoaded', true);

    let pubDate = this.$store.state.systemInfo.publishedToDate
    if(pubDate)
      this.setDefaultStartDateEndDate(pubDate);
    this.loadingFormData = false;
  },
  methods: {
    ...mapActions(['resetReachState']),
     formatRange(filter) {
      let { from, to, greaterThan, lessThan } = filter;
      from = !from ? "" : from;
      to = !to ? "" : to;

      // Case: < X
      if (lessThan && to !== undefined) {
        return `<${to}`;
      }

      // Case: > X
      if (greaterThan && from !== undefined) {
        return `>${from}`;
      }
      if (from === null && to === null) {
        return "";
      }
      // Case: X–Y
      if (!lessThan && !greaterThan && from && to) {
        return `${from}-${to}`;
      }

      return "";
    },
    setDefaultPayload() {
        this.$store.commit('searchApiBody/setDefaultSearchPayload', this.$store.state.searchApiBody.payload);
    },
    async handleScheduleReportSubmit(reportname) {
      
      this.loadingScheduleReport = true
      try {
          let payload = {...this.$store.state.searchApiBody };
          
          // kör frågan här
          await createScheduledReportQuery(reportname,payload);

          this.$bvToast.toast("Scheduled run, you will receive an email when it is complete.", {
              title: "Scheduled run",
              variant: "success",
              solid: true,
              autoHideDelay: 5000,
          })
      } catch (error) {
          this.loadingScheduleReport = false
          this.showScheduleReportModal = false
          this.$bvToast.toast("Could not schedule the report run", {
              title: "Server error",
              variant: "danger",
              solid: true,
              autoHideDelay: 5000,
          })
      } finally {
          this.loadingScheduleReport = false
          this.showScheduleReportModal = false
      }

    },
    async handleExportResults() {
      try {
        this.loadingExportResults = true;
        let payload = {...this.$store.state.searchApiBody };
        await downloadFile('QueryData/DownloadExport', payload);
      } catch (error) {
        console.error('Failed to export results:', error);
        this.$bvToast.toast('An error occurred during data export. Please try again later.', {
          title: 'Export failed', 
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loadingExportResults = false;
        this.showSlowQueryModal = false;
      }
    },

    resetPubDate(){
      let updatedPayload = {
        ...this.$store.getters["searchApiBody/getPayload"],
        pub_start_date: "",
        pub_end_date: "",
        showPubDate: false,
        selectedPeriod: "consumptionperiod",
      };
      this.$store.commit(
        'searchApiBody/updatePayload',
        updatedPayload,
      );
    },
    getNumberOfStoredGenres() {
      
      return this.payload.GenreConfigs.length;
    },
    removeTargets(){
      this.goToListTab = true;
      const updatedPayload = {
        ...this.$store.getters["searchApiBody/getPayload"],
        measures: []
      };
      this.$store.commit(
        'searchApiBody/updatePayload',
        updatedPayload,
      );            
    },
    setDefaultStartDateEndDate(pubDate){
      let newStartDate = addDays(pubDate, -2);
      this.$store.commit('searchApiBody/updateStartDate', newStartDate);
      this.$store.commit("searchApiBody/updateEndDate", pubDate);
    },
    getTargetGroupCount() {

      return this.payload.measures.length
    },
    removeGenres() {
      const updatedPayload = {
        ...this.$store.getters["searchApiBody/getPayload"],
        GenreConfigs: []
      };
      this.$store.commit(
        'searchApiBody/updatePayload',
        updatedPayload,
      );            
    },
    clearAllFields()
    {
      //reset selectedperiod
      this.$store.commit('searchApiBody/updatePeriod', 'consumptionperiod');
      //reset from and to date
      this.$store.commit('searchApiBody/updateEndDate',this.$store.state.systemInfo.publishedToDate);

      let newStartDate = addDays(this.$store.state.systemInfo.publishedToDate, -2);
      this.$store.commit('searchApiBody/updateStartDate', newStartDate);
      //reset measures
      this.removeTargets();
      //reset title
      this.title_search = "";
      //reset genre
      this.removeGenres();
      this.resetPubDate();
      //clear extra fields
      this.updateTitleLength("");
      this.updateMeasuredDays("");
      this.updateTotalStarts("");
      this.updateTotalOnlineRating("");
      this.form.filters.title_length = "";
      this.form.filters.measured_days = "";
      this.form.filters.starts = "";
      this.form.filters.total_online_rating = "";
      //reset options
      this.form.categories.resetDefaultSelected = true;
      this.form.sites.resetDefaultSelected = true;
      this.form.columns.resetDefaultSelected = true;
      let siteValues = this.form.sites.options.map(s => s.value)
      this.updateSiteIds(siteValues);
    },
    async setDefaultSites()
    {     
        this.form.sites.options = [];
        let sitesResponse = await fetchFormFieldData('Sites', 'Program');
        if (sitesResponse.statusCode) {
          this.$bvToast.toast(sitesResponse.errorMessage, {
            title: 'Warning',
            variant: 'warning',
            solid: true,
            autoHideDelay: 3000,
          });
        } else {
          for (let index in sitesResponse) {
            let site = {
              text: sitesResponse[index].site_name,
              value: sitesResponse[index].site_id,
              selected: true,
            };
            this.form.sites.options.push(site);
          }               
          this.loadingFormDataValue = 25;
        }                        
    },
    formatDate(date) {
      return dateToyymmddFormat(date);
    },
    updateTitleLength(value) {
      this.updateFilter(value, 'searchApiBody/updateTitleLength', 'title_length');
    },
    updateMeasuredDays(value) {
      this.updateFilter(value, 'searchApiBody/updateMeasuredDays', 'measured_days');
    },
    updateTotalOnlineRating(value) {
      this.updateFilter(value, 'searchApiBody/updateTotalOnlineRating', 'total_online_rating');
    },
    //används ej ska den tas bort?
    updateTvRating(value) {
      this.updateFilter(value, 'searchApiBody/updateTvRating', 'tv_rating');
    },
    updateTotalStarts(value) {
      this.updateFilter(value, 'searchApiBody/updateTotalStarts', 'starts');
    },
    updateSiteIds(value) {      
      this.$store.commit('searchApiBody/updateSiteIds', value);
    },
    updateColumns(value) {
      this.$store.commit('searchApiBody/updateColumns', value);
    },
    updateCategoryIds(value) {
      this.$store.commit('searchApiBody/updateCategoryIds', value);
    },
    updateIsWildCard(value) {
      this.$store.commit('searchApiBody/updateIsWildcard', value);
    },
    async setupGenreOptions() {
      let genres = await fetchFormFieldData('Genres');
      if (genres.statusCode) {
        this.$bvToast.toast(genres.errorMessage, {
          title: 'Warning',
          variant: 'warning',
          solid: true,
          autoHideDelay: 3000,
        });
      }

      this.form.genres.genreOptionArray = genres;
    },
    
    loadWholeTable(load) {
      this.loadingSearchResults = load;
    },
    async submitSort() {
      this.loadingSearchResults = true;
      
      // empty searchApiBody.payload.title_codes if it exists
      let apiBody = this.$store.state.searchApiBody;
      if (apiBody.payload.title_codes) {  
        apiBody.payload.title_codes = [];
      }


      
      // Store current selections before the sort
      const selectedRows = this.$store.state.searchResults.results
        .filter(item => item.valda)
        .map(item => item.rowNum);
      
      
      try {
        const response = await search('QueryData', apiBody);
        this.$store.commit('searchResults/updateRowCount', response.info.total_row_count);
        
        // Process results but preserve selections
        
        processResults(response, this.$store);
        
        // Restore selections based on rowNum
        selectedRows.forEach(rowNum => {
          this.$store.commit('searchResults/select', rowNum);
        });
        
      } catch (error) {
        this.$bvToast.toast('Unknown server error during search result retrieval', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 10000,
        });  
        this.$store.commit('searchResults/updateResults', []);
        this.$store.commit('searchResults/updateResultFields', []);
      } finally {
        this.loadingSearchResults = false;
      }
    },

    async submitExcludeRows() {
      this.loadingSearchResults = true;
      let apiBody = this.$store.state.searchApiBody;
      if (apiBody.payload.title_codes) {
        apiBody.payload.title_codes = [];
      }
      
      try {
        const response = await search('QueryData', apiBody);
        this.$store.commit('searchResults/updateRowCount', response.info.total_row_count);
        this.$store.commit('searchResults/updateHasExcludedRows', true);
        
        // Process results without preserving selections since they're excluded
        processResults(response, this.$store);
        
      } catch (error) {
        this.$bvToast.toast('Unknown server error during search result retrieval', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 10000,
        });
        this.$store.commit('searchResults/updateResults', []);
        this.$store.commit('searchResults/updateResultFields', []);
      } finally {
        this.loadingSearchResults = false;
      }
    },

    async submit() {
      if (
        this.$store.state.searchApiBody.payload.start_date >
        this.$store.state.searchApiBody.payload.end_date
      ) {
        this.$bvToast.toast('From-date must not be greater than to-date.', {
          title: 'Invalid search period',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
          bodyOutputType: 'trustedHtml',
        });
        return;
      }
      if (
        this.$store.state.searchApiBody.payload.start_date <
        this.$store.state.systemInfo.publishedFromDate
      ) {
        this.$bvToast.toast(
          'The period must not start before ' + this.$store.state.systemInfo.publishedFromDate + '.',
          {
            title: 'Invalid search period',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000,
            bodyOutputType: 'trustedHtml',
          },
        );
        return;
      }
      if (
        this.$store.state.searchApiBody.payload.end_date >
        this.$store.state.systemInfo.publishedToDate
      ) {
        this.$bvToast.toast(
          'To-date must not be greater than the latest publication date which is ' +
            this.$store.state.systemInfo.publishedToDate +
            '.',
          {
            title: 'Invalid search period',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000,
            bodyOutputType: 'trustedHtml',
          },
        );
        return;
      }
      if (
        this.$store.state.searchApiBody.payload.start_date.length > 0 &&
        this.$store.state.searchApiBody.payload.end_date.length > 0 &&
        !this.loadingSearchResults
      ) {
        this.formValidator.dateFrom = true;
        this.formValidator.dateTo = true;
        this.formValidator.title = null;
        this.initFirstSearch = true;
        this.loadingSearchResults = true;
        let apiBody = this.$store.state.searchApiBody;
        // bus.$emit('searching', 713);
        let filteredMeasuresByPeriod = filterMeasuresByPeriod(
          apiBody.payload.measures,
          this.selectedPeriod,
        );
        if (filteredMeasuresByPeriod.length > 0) {
          this.$store.commit('searchApiBody/updateSortDirection', 'DESC');
          this.$store.commit('searchApiBody/updateSortBy', 'measure_1');
        } else {
          this.$store.commit('searchApiBody/updateSortDirection', 'ASC');
          this.$store.commit('searchApiBody/updateSortBy', 'published_at');
        }
        this.$store.commit('searchApiBody/updateTitleKeyStart', 0);
        this.$store.commit(
          'searchApiBody/updatePublishedToDate',
          this.$store.state.systemInfo.publishedToDate,
        );
        let searchResults = null;
        this.resetReachState();
        this.$store.commit('searchResults/setSumVisible', false);
        this.$store.commit('searchApiBody/updateExcludedRows', []);

        // get querycost
        if (apiBody.payload.title_codes) {
          apiBody.payload.title_codes = [];
        } 
        

        this.$store.commit('searchResults/updateResults', []);
        this.$store.commit('searchResults/updateResultFields', []);
        
        await search('QueryData/QueryCost', apiBody)
          .then(async queryCost => {

            if(queryCost.queryClass == 'Normal') {

                await search('QueryData', apiBody)
                .then((response) => {
                  searchResults = response;
                  this.$store.commit('searchResults/updateRowCount', response.info.total_row_count);
                  this.$store.commit('searchResults/updateHasExcludedRows', false);
                  this.$store.commit('reachApiBody/emptyTitleCodes');

                  // Locking in the underlying query for use in the consumption tab
                  this.$store.commit('consumptionApiBody/setConsumptionApiBody', { ...this.$store.state.searchApiBody.payload })
                  // Set dates array for the query period
                  let queryDates = []
                  try {
                      queryDates = getUniqueDates(this.$store.getters['consumptionApiBody/consumptionApiBody'].start_date, this.$store.getters['consumptionApiBody/consumptionApiBody'].end_date)
                  } catch (error) {
                      console.error(error.message);
                  }
                  this.$store.commit('consumptionApiBody/updateUniqueDatesInPeriod', queryDates)
                  const consumptionApiBody = JSON.parse(
                    JSON.stringify(this.$store.getters['consumptionApiBody/consumptionApiBody']),
                  );
                  this.$store.commit('consumptionResults/setConsumptionResultsQuery', consumptionApiBody);
                  
                  processResults(searchResults, this.$store);
                })
                .catch(() => {
                  this.loadingSearchResults = false;
                  this.$bvToast.toast('Unknown server error during result retrieval', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true,
                    autoHideDelay: 10000,
                  });
                  this.$store.commit('searchResults/updateResults', []);
                  this.$store.commit('searchResults/updateResultFields', []);
                  return;
                });
          } else if (queryCost.queryClass == 'RowLimitExceeded') {
                  this.loadingSearchResults = false;
                  this.$store.commit('searchResults/updateResults', []);
                  this.$store.commit('searchResults/updateResultFields', []);
                  this.showSlowQueryModal = true;
                  return;

          } else if (queryCost.queryClass == 'Schedule') {
                  this.loadingSearchResults = false;
                  this.$store.commit('searchResults/updateResults', []);
                  this.$store.commit('searchResults/updateResultFields', []);
                  this.showScheduleReportModal = true;
                  return;

          } else if (queryCost.queryClass == 'Forbidden') {
                  this.loadingSearchResults = false;
                  this.$bvToast.toast('The run is too extensive', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true,
                    autoHideDelay: 10000,
                  });
                  this.$store.commit('searchResults/updateResults', []);
                  this.$store.commit('searchResults/updateResultFields', []);
                  return;

          } 




          })
          
        
        this.loadingSearchResults = false;
      }
      else {
        this.formValidator.dateFrom = this.$store.state.searchApiBody.payload.start_date.length > 0;
        this.formValidator.dateTo = this.$store.state.searchApiBody.payload.end_date.length > 0;
      }
    },
    updateFilter(value, store, validator) {
      this.validationError = false;
      // If value match for example >100
      if (value.match('^>[0-9]*$')) {
        let numberString = value.substr(1, value.length - 1);
        this.$store.commit(store, {
          from: parseInt(numberString),
          greaterThan: true,
          lessThan: false,
        });
        this.formValidator[validator] = null;
      }
      // If value match for example <100
      else if (value.match('^<[0-9]*$')) {
        let numberString = value.substr(1, value.length - 1);
        this.$store.commit(store, {
          from: 0,
          to: parseInt(numberString),
          greaterThan: false,
          lessThan: true,
        });
        this.formValidator[validator] = null;
      }
      // If value match for example 100-200
      else if (value.match('^[0-9]*-[0-9]*$')) {
        let numberSplit = value.split('-');
        this.$store.commit(store, {
          from: parseInt(numberSplit[0]),
          to: parseInt(numberSplit[1]),
          greaterThan: null,
          lessThan: null,
        });
        this.formValidator[validator] = null;
      }
      // If value is undefined (accepted)
      else if (value == '') {
        this.$store.commit(store, {
          from: null,
          to: null,
          greaterThan: null,
          lessThan: null,
        });
        this.formValidator[validator] = null;
      }
      // If value does not match for example >100, <100 or 100-200 (not accepted)
      else {
        this.formValidator[validator] = false;
        this.validationError = true;
      }
    },
    onKeyUp(event) {
      if (event.key === "Enter" && this.allModalsClosed) {
        this.submit();
      }
    },
   
  },
};
</script>

<style scoped>
.inputwidthshowmorefields{
  max-width: 15rem;
}
>>> .showMoreFilteringButton{
  width: 8.3rem;
}
label{
  margin-bottom: 0.25rem
}
.cbxWildcard {
  -ms-transform: scale(0.8); /* IE */
  -moz-transform: scale(0.8); /* FF */
  -webkit-transform: scale(0.8); /* Safari and Chrome */
  -o-transform: scale(0.8); /* Opera */
  transform: scale(0.8);
  float: right;
  min-height: 0.5rem;
  margin-bottom: -0.3rem;
  font-size: 0.8em;
  margin-top: -0.3rem;
  margin-left: 0.3rem;
}
.wildcardContainer {
  float: right;
  margin-top: 0.5rem;
  height: 0.8rem;
}
.wildcardCbxText {
  font-weight: 400;
  font-size: 0.56rem;
  color: #495057;
}
.wildcardCbxIcon {
  transform: scale(1.4);
  margin-left: 0.13rem;
}
.supportTextNoAnswers {
  font-size: 0.8em;
  font-style: italic;
}
.showMore {
  width: 100%;
  background-color: aliceblue;
  color: #000000;
}
</style>
